import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { getReviewSurvey, submitReviewSurvey } from "@/lib/heallexa-review.functions";

type Search = { token?: string; providerId?: string; event?: string };

export const Route = createFileRoute("/review-heallexa")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    token: typeof s.token === "string" ? s.token : undefined,
    providerId: typeof s.providerId === "string" ? s.providerId : undefined,
    event: typeof s.event === "string" ? s.event : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Rate your experience with Heallexa" },
      { name: "description", content: "Share your feedback about Heallexa." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ReviewSurveyPage,
});

function ReviewSurveyPage() {
  const { token } = Route.useSearch();
  const fetchSurvey = useServerFn(getReviewSurvey);
  const submitSurvey = useServerFn(submitReviewSurvey);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<{ providerName: string | null; alreadySubmitted: boolean; googleReviewUrl: string } | null>(null);
  const [hoverRating, setHoverRating] = useState(0);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing review token."); setLoading(false); return; }
    fetchSurvey({ data: { token } })
      .then((r) => {
        if (!r.ok) setError("This review link is invalid or expired.");
        else setInfo({ providerName: r.providerName, alreadySubmitted: r.alreadySubmitted, googleReviewUrl: r.googleReviewUrl });
      })
      .catch(() => setError("Unable to load the survey. Please try again later."))
      .finally(() => setLoading(false));
  }, [token, fetchSurvey]);

  const handleSelectRating = async (value: number) => {
    if (!token || submitting || done) return;
    setRating(value);
    if (value >= 4) {
      setSubmitting(true);
      try {
        const res = await submitSurvey({
          data: { token, rating: value, userAgent: navigator.userAgent.slice(0, 500) },
        });
        if (typeof window !== "undefined") {
          window.location.href = res.googleReviewUrl;
        }
      } catch {
        setSubmitting(false);
        setError("Could not save your rating. Please try again.");
      }
    }
  };

  const submitLowRating = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      await submitSurvey({
        data: { token, rating, feedback, userAgent: navigator.userAgent.slice(0, 500) },
      });
      setDone(true);
    } catch {
      setError("Could not submit your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-32 md:pt-24 pb-16 mx-auto max-w-xl px-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="card-surface rounded-md p-8 text-center">
            <h1 className="font-display text-xl font-semibold mb-2">Survey unavailable</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : info?.alreadySubmitted ? (
          <div className="card-surface rounded-md border-primary/40 bg-primary/5 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold mb-2">Thanks — already received</h1>
            <p className="text-sm text-muted-foreground">We already have your feedback. Thank you for helping us improve Heallexa.</p>
          </div>
        ) : done ? (
          <div className="card-surface rounded-md border-primary/40 bg-primary/5 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold mb-2">Thank you for your feedback</h1>
            <p className="text-sm text-muted-foreground">We appreciate your help in improving Heallexa.</p>
          </div>
        ) : (
          <div className="card-surface rounded-md p-8">
            <h1 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground text-center">
              How was your experience with Heallexa?
            </h1>
            <p className="mt-3 text-[14px] text-muted-foreground text-center">
              Your feedback helps us improve our platform for healthcare providers.
            </p>

            <div className="mt-8 flex items-center justify-center gap-2 sm:gap-3" role="radiogroup" aria-label="Rate from 1 to 5 stars">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hoverRating || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={rating === n}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    disabled={submitting}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => handleSelectRating(n)}
                    className="p-2 rounded-md hover:bg-muted disabled:opacity-60 transition"
                  >
                    <Star
                      className={`h-10 w-10 sm:h-12 sm:w-12 transition ${
                        active ? "fill-yellow-400 text-yellow-400" : "fill-muted text-muted-foreground/30"
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {rating > 0 && rating <= 3 && !submitting && (
              <div className="mt-8 border-t border-border pt-6">
                <h2 className="text-[clamp(1.4rem,2.2vw,1.75rem)] font-medium tracking-[-0.02em] text-foreground">How can we improve?</h2>
                <p className="mt-2 text-[14px] text-muted-foreground">
                  We're sorry your experience was not perfect. Please tell us what we can do better.
                </p>
                <Textarea
                  className="mt-4"
                  rows={5}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Your feedback..."
                />
                <Button onClick={submitLowRating} disabled={submitting} className="w-full mt-4" size="lg">
                  {submitting ? "Submitting…" : "Submit Feedback"}
                </Button>
              </div>
            )}

            {submitting && rating >= 4 && (
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Thanks! Redirecting you to leave a Google review…
              </p>
            )}
          </div>
        )}
      </main>
      <FooterSection />
    </div>
  );
}
